// Copyright (c) 2026 Or2 LLC. All rights reserved.
// Insight Platform - proprietary software by Nnaico (https://nnaico.com).
// See the LICENSE file in this repository. All rights reserved.

import { useMemo, useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CodeIcon from '@mui/icons-material/Code';
import hljs from 'highlight.js/lib/common';
import 'highlight.js/styles/github.css';
import '../hljs-github-dark.css'; // same theme, scoped under .dark / [data-mui-color-scheme=dark]

// Human-readable labels for the fence languages the general assistant
// actually emits. Unknown languages fall back to the raw tag.
const LANGUAGE_LABELS = {
  md: 'Markdown', markdown: 'Markdown',
  js: 'JavaScript', javascript: 'JavaScript', jsx: 'JSX',
  ts: 'TypeScript', typescript: 'TypeScript', tsx: 'TSX',
  py: 'Python', python: 'Python',
  sql: 'SQL', json: 'JSON', bash: 'Bash', shell: 'Shell', sh: 'Shell',
  html: 'HTML', xml: 'XML', css: 'CSS', yaml: 'YAML', yml: 'YAML',
  diff: 'Diff', ini: 'INI', csv: 'CSV', plaintext: 'Text', text: 'Text',
};

// Extract (language, code) from what react-markdown passes to <pre>:
// children is the single <code className="language-x">…</code> element.
const parseFence = (children) => {
  const child = Array.isArray(children) ? children[0] : children;
  if (child && typeof child === 'object' && child.props) {
    const cls = child.props.className || '';
    const m = cls.match(/language-([\w-]+)/);
    const code = String(child.props.children ?? '').replace(/\n$/, '');
    return { language: m ? m[1].toLowerCase() : '', code };
  }
  return { language: '', code: String(children ?? '') };
};

export const CodeBlock = ({ children, renderPreview }) => {
  const { language, code } = parseFence(children);
  // Markdown blocks can be rendered as formatted preview (toggle in header);
  // renderPreview is provided by the markdown pipeline (avoids import cycles).
  const canPreview = Boolean(renderPreview) && ['md', 'markdown'].includes(language);
  const [copied, setCopied] = useState(false);
  const [showPreview, setShowPreview] = useState(canPreview); // markdown defaults to preview

  const highlighted = useMemo(() => {
    try {
      if (language && hljs.getLanguage(language)) {
        return hljs.highlight(code, { language }).value;
      }
      if (code.length < 20000) {
        return hljs.highlightAuto(code).value;
      }
    } catch {
      // fall through: never break the message over a highlight error
    }
    return code.replace(/[&<>]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]));
  }, [language, code]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable (permissions/http): silent, the button just
      // doesn't confirm
    }
  };

  const label = LANGUAGE_LABELS[language] || (language || '').toUpperCase() || 'CODE';

  return (
    <Box
      className="insight-codeblock"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        my: 1.5,
        bgcolor: 'transparent',
      }}
    >
      {/* Header: language label + copy */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'rgba(128, 128, 128, 0.08)',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.05em', fontSize: '0.65rem', textTransform: 'uppercase' }}
        >
          {label}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
          {canPreview && (
            <Tooltip title={showPreview ? 'View source' : 'Rendered preview'} placement="top">
              <IconButton
                size="small"
                onClick={() => setShowPreview(v => !v)}
                sx={{
                  p: 0.5,
                  color: showPreview ? 'primary.main' : 'text.secondary',
                  bgcolor: showPreview ? 'action.selected' : 'transparent',
                }}
              >
                {showPreview ? <CodeIcon sx={{ fontSize: 15 }} /> : <VisibilityIcon sx={{ fontSize: 15 }} />}
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={copied ? 'Copied!' : 'Copy code'} placement="left">
            <IconButton size="small" onClick={handleCopy} sx={{ p: 0.5, color: 'text.secondary' }}>
              {copied ? <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Body: rendered preview for markdown, highlighted source otherwise */}
      {canPreview && showPreview ? (
        <Box className="aui-md" sx={{ px: 1.5, py: 1, overflowX: 'auto' }}>
          {renderPreview(code)}
        </Box>
      ) : (
        <pre className="insight-codeblock-pre">
          <code className="hljs" dangerouslySetInnerHTML={{ __html: highlighted }} />
        </pre>
      )}
    </Box>
  );
};
